import React, { useId, useRef } from 'react';
import {
  Button, Menu, MenuItem, MenuTrigger, Popover,
} from 'react-aria-components';
import customIcons from '../../utils/icons';

export interface DropdownMenuItem {
  id: string,
  label: string,
  icon?: React.ReactNode,
  onAction: () => void | Promise<void>,
}

export interface DropdownMenuProps {
  label: string, // trigger text, e.g. "Take Action"
  icon?: React.ReactNode, // trigger icon
  items: DropdownMenuItem[],
  isDisabled?: boolean,
  footer?: React.ReactNode,
}

const triggerClasses = 'group inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-pipbg-subtle bg-white '
  + 'text-body-regular font-semibold text-piptext-default cursor-pointer '
  + 'shadow-outlinedBottomShadow '
  + 'hover:bg-pipbg-subtle disabled:cursor-not-allowed disabled:text-pipgrey';

const itemClasses = 'flex items-center gap-2 px-3 py-2 whitespace-nowrap rounded-md border border-transparent outline-none '
  + 'text-body-regular font-semibold text-piptext-default cursor-pointer '
  + 'hover:bg-pipbg-subtle focus:bg-pipbg-subtle pressed:bg-pipbg-pressed pressed:border-pipbg-grouped';

// Button that opens a menu of items passed in by the caller. Display only: each item's onAction decides what it does
export default function DropdownMenu({
  label, icon, items, isDisabled = false, footer,
}: Readonly<DropdownMenuProps>) {
  const footerId = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  return (
    <MenuTrigger>
      <Button isDisabled={isDisabled} className={triggerClasses}>
        {icon && <span aria-hidden="true" className="flex text-base">{icon}</span>}
        <span>{label}</span>
        <span aria-hidden="true" className="flex text-base transition-transform group-aria-expanded:rotate-180">
          {customIcons.chevronDown}
        </span>
      </Button>
      {/* Using scrollRef to measure the Menu and footer together stops the footer from being pushed off screen */}
      <Popover
        placement="bottom end"
        offset={8}
        scrollRef={contentRef}
        className="flex flex-col min-w-[187px] w-max p-1 rounded-lg border border-pipbg-subtle bg-white shadow-outlinedBottomShadow"
      >
        <div ref={contentRef} className="min-h-0 overflow-auto">
          <Menu aria-describedby={footer ? footerId : undefined} className="outline-none">
            {items.map((item) => (
              <MenuItem key={item.id} id={item.id} textValue={item.label} onAction={item.onAction} className={itemClasses}>
                {item.icon && <span aria-hidden="true" className="flex text-base">{item.icon}</span>}
                {item.label}
              </MenuItem>
            ))}
          </Menu>
          {footer && (
            <div className="mt-1 pt-1 border-t border-pipbg-grouped">
              <div
                id={footerId}
                className="px-3 py-2 border border-transparent text-body-regular font-semibold text-piptext-default"
              >
                {footer}
              </div>
            </div>
          )}
        </div>
      </Popover>
    </MenuTrigger>
  );
}
